// ─────────────────────────────────────────────
//  App.js — root routing for ATC web app
//
//  Anesthesia Team Control (ATC)
//  Copyright © 2026 Gonzalo Martinez (gamc00@gmail.com). All rights reserved.
//  Proprietary and confidential. Not licensed for public use — see /LICENSE.
// ─────────────────────────────────────────────
import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ToastProvider } from "./context/ToastContext";
import { AlertProvider } from "./context/AlertContext";
import { MessagingProvider } from "./context/MessagingContext";
import PreviewBanner  from "./components/PreviewBanner";
import UpdateBanner from "./components/UpdateBanner";
import TestModeBanner from "./features/testing/TestModeBanner";
import ErrorBoundary  from "./components/ErrorBoundary";
import MessagingLauncher from "./components/MessagingLauncher";
import BiometricGate  from "./native/BiometricGate";
import NativeBridge   from "./native/NativeBridge";
import MobileShellGate from "./native/MobileShellGate";

// Pages
import LoginPage      from "./pages/LoginPage";
import GetStartedPage from "./pages/GetStartedPage";
import WelcomePage    from "./pages/WelcomePage";
import GoLivePage     from "./pages/GoLivePage";
import NoAccessPage   from "./pages/NoAccessPage";
import HomePage       from "./pages/HomePage";
import BoardPage      from "./pages/BoardPage";
import ProviderPage   from "./pages/ProviderPage";
import RoomStatusScreen from "./pages/mobile/RoomStatusScreen";
import MessagesPage   from "./pages/MessagesPage";
import TeamPage       from "./pages/TeamPage";
import TimelineLightPage from "./pages/TimelineLightPage";
import SetupPage      from "./pages/SetupPage";
import DailyAssignmentPage from "./pages/DailyAssignmentPage";
import TimelineChartPage from "./pages/TimelineChartPage";
import BoardCPage from "./pages/BoardCPage";
import BoardAPage from "./pages/BoardAPage";
import BoardBPage from "./pages/BoardBPage";
import ProfilePage    from "./pages/ProfilePage";
import SchedulePage   from "./pages/SchedulePage";
import NotFoundPage   from "./pages/NotFoundPage";
import PrimaryFacilityPrompt from "./components/PrimaryFacilityPrompt";
import GeofenceGate from "./components/GeofenceGate";

// Protected route wrapper
function Protected({ children }) {
  const { user } = useAuth();
  return user ? children : <Navigate to="/login" replace />;
}

// A Scheduler's world is the scheduling module — clinical/board pages
// redirect there (their writes are rules-denied anyway; this keeps the UI honest).
function NotScheduler({ children }) {
  const { user, isScheduler } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  return isScheduler ? <Navigate to="/schedule" replace /> : children;
}

// A Board Manager is a shared wall device — its whole world is the OR board.
// Every other page bounces back there (writes are rules-scoped anyway).
function NotBoardManager({ children }) {
  const { user, isBoardManager } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  return isBoardManager ? <Navigate to="/board" replace /> : children;
}

// Redirect logged-in users away from login.
//
// Signing in lands on the dashboard, not the board. The two roles who must NOT
// see a dashboard are not special-cased here — /home is wrapped in the same
// NotScheduler + NotBoardManager gates as every other clinical page, so a
// Scheduler continues to /schedule and a wall device continues to /board on
// their own. One landing rule, no second copy of the role logic.
function PublicOnly({ children }) {
  const { user } = useAuth();
  return !user ? children : <Navigate to="/home" replace />;
}

// Two people should never reach a clinical page, for opposite reasons.
//
// A sandbox user who has not finished the wizard has no rooms and no staff
// yet, so every board and list would render empty — indistinguishable from a
// broken app. They go to the wizard, which is also where their facility gets
// created.
//
// A real user with no facility can read NOTHING: facility membership is the
// entire authorization model, and self-provisioning no longer grants any
// (see isSafeSelfProfile in firestore.rules). Rather than a board that fails
// every query, they get a sentence explaining that an administrator has to add
// them.
//
// Absent facilityIds counts as no facility. Failing closed on garbage data is
// deliberate — this codebase has been bitten by treating a missing value as a
// permissive one.
//
// Exported for testing so the gate can be exercised without mounting the whole
// app, which would pull in Firebase, messaging and the native bridge.
export function OnboardingGate({ children, user: injected }) {
  const ctx = useAuth();
  const user = injected !== undefined ? injected : ctx.user;
  // Not signed in — Protected already handles the redirect; don't fight it.
  if (!user) return children;
  if (user.sandbox) {
    return user.onboarded ? children : <Navigate to="/welcome" replace />;
  }
  return (user.facilityIds || []).length
    ? children
    : <Navigate to="/no-access" replace />;
}

export default function App() {
  return (
    <ToastProvider>
      {/* Phase 6: native-only biometric app-lock; renders children as-is on web */}
      <BiometricGate>
      <AuthProvider>
        <ErrorBoundary>
        <AlertProvider>
        {/* One messaging subscription set, shared by every screen. Inside
            AuthProvider so it sees the signed-in user + today's roster. The chat
            surface itself now lives inline on the provider's "My Room" page
            (ProviderPage → "Messages" card), not a floating launcher. */}
        <MessagingProvider>
        {/* Phase 6: FCM push registration on native (no-op on web) */}
        <NativeBridge />
        {/* ABOVE PreviewBanner on purpose: WHICH DAY you are looking at
            outranks which person you are wearing. Testing mode always previews
            somebody, so both bars can be on at once — they are different
            colours so they read as two facts, not one repeated warning. */}
        {/* ABOVE the identity banners on purpose: "this tab is running an old
            build" outranks "you are previewing as someone", because a stale
            bundle can be the reason the screen looks wrong at all. Renders
            nothing until a deploy actually lands, and never reloads by itself —
            see components/UpdateBanner.js. */}
        <UpdateBanner />
        <TestModeBanner />
        <PreviewBanner />
        {/* First-login question: "Which facility do you work at most often?" —
            stores users.primaryFacilityId, which orders provider dropdowns. */}
        <PrimaryFacilityPrompt />
        {/* Urgency is no longer a global toast — it surfaces only in My Rooms on
            the affected room card (visible to that room's doctor + CRNA). */}
        {/* Board Manager device geofence: blocks off-site use of the shared
            per-facility TV accounts; renders everyone else untouched. */}
        <GeofenceGate>
        <BrowserRouter>
          <MobileShellGate>
          <Routes>
          <Route path="/login" element={<PublicOnly><LoginPage /></PublicOnly>} />

          {/* Sign-up path. Deliberately NOT wrapped in Protected: both screens
              are reachable before an account exists, and each redirects to
              /login itself when it lacks the credentials it needs.
              /get-started is where the Firebase Auth account is finally
              created, after the sandbox-or-organization choice — never before,
              or AuthContext would bootstrap a profile into the real database
              while the app is still pointed at it. */}
          <Route path="/get-started" element={<GetStartedPage />} />
          <Route path="/go-live" element={<GoLivePage />} />

          {/* Ungated on purpose — these two ARE where the gate sends people, so
              gating them would loop. */}
          <Route path="/welcome" element={<Protected><WelcomePage /></Protected>} />
          <Route path="/no-access" element={<Protected><NoAccessPage /></Protected>} />

          {/* The post-login landing — four doors onto the day (board, my
              rooms, timeline, messages) plus a wide plaque onto the department
              directory (team). Same gates as every clinical page, which is what
              keeps PublicOnly above down to a single destination. */}
          <Route path="/home" element={<Protected><OnboardingGate><NotScheduler><NotBoardManager><HomePage /></NotBoardManager></NotScheduler></OnboardingGate></Protected>} />

          <Route path="/board" element={<Protected><OnboardingGate><NotScheduler><BoardPage /></NotScheduler></OnboardingGate></Protected>} />
          <Route path="/room"  element={<Protected><OnboardingGate><NotScheduler><NotBoardManager><ProviderPage /></NotBoardManager></NotScheduler></OnboardingGate></Protected>} />
          {/* Room Status — the four case phases, stamped by the provider and
              acknowledged by the supervisor. It existed only inside the mobile
              shell; owner asked for it in the navigation menu (2026-09-07), and
              a menu entry needs a route to open. Guarded exactly like /room: a
              personal surface, so not the Scheduler and not the shared wall
              device. WHO SEES WHICH CONTROLS is decided per room by
              domain/caseRole, not by this route — a supervisor opening it gets
              Acknowledge, a provider gets the stamps, anyone else read-only. */}
          <Route path="/room-status" element={<Protected><OnboardingGate><NotScheduler><NotBoardManager><RoomStatusScreen /></NotBoardManager></NotScheduler></OnboardingGate></Protected>} />
          {/* /messages is the message CENTRE again (the dashboard's fourth
              door), rebuilt on the same ChannelList + ConversationView the 💬
              panel uses — not the old standalone DM screen, which had its own
              diverging copy of chat and was redirected away for that reason.
              Board Managers have messaging revoked in the rules, so the gate
              here matches the rule rather than relying on the page to hide it. */}
          <Route path="/messages" element={<Protected><OnboardingGate><NotScheduler><NotBoardManager><MessagesPage /></NotBoardManager></NotScheduler></OnboardingGate></Protected>} />
          {/* The tenant-wide contact directory (the landing's fifth door).
              READ ONLY: it renders one Firestore document the scheduled sync
              writes, and cross-references it against `staff` in memory.

              ⚠️ THE SAME GATES AS /messages, AND FOR THE SAME REASON. It holds
              ~466 personal mobile numbers, so the shared wall device is bounced
              here as well as denied by the rules — a corridor television must
              never be able to render the department's phone book. */}
          <Route path="/team" element={<Protected><OnboardingGate><NotScheduler><NotBoardManager><TeamPage /></NotBoardManager></NotScheduler></OnboardingGate></Protected>} />
          {/* Daily Assignments is a scheduling surface — Admins + Schedulers
              only (page guard + rules enforce; deliberately NOT NotScheduler-
              wrapped so schedulers can reach it). */}
          <Route path="/daily-list" element={<Protected><OnboardingGate><NotBoardManager><DailyAssignmentPage /></NotBoardManager></OnboardingGate></Protected>} />

          {/* Timeline is one of the Board Manager's two surfaces (TV board +
              timeline) — deliberately NOT wrapped in NotBoardManager. */}
          <Route path="/timeline" element={<Protected><OnboardingGate><NotScheduler><TimelineChartPage /></NotScheduler></OnboardingGate></Protected>} />
          {/* The read-only Timeline for CRNAs, AAs and residents. Its own route
              rather than the full chart with the controls hidden — the full page
              WRITES assignments, and a hidden control is one bug from reachable.
              The dashboard picks between the two (HomePage.timelineRouteFor);
              nothing stops a doctor opening this one directly. */}
          <Route path="/timeline-light" element={<Protected><OnboardingGate><NotScheduler><NotBoardManager><TimelineLightPage /></NotBoardManager></NotScheduler></OnboardingGate></Protected>} />
          {/* Three alternative board layouts, trialled alongside the original.
              /panel stays working so an open tab or bookmark does not break. */}
          <Route path="/panel"   element={<Protected><OnboardingGate><NotScheduler><BoardCPage /></NotScheduler></OnboardingGate></Protected>} />
          <Route path="/board-c" element={<Protected><OnboardingGate><NotScheduler><BoardCPage /></NotScheduler></OnboardingGate></Protected>} />
          <Route path="/board-a" element={<Protected><OnboardingGate><NotScheduler><BoardAPage /></NotScheduler></OnboardingGate></Protected>} />
          <Route path="/board-b" element={<Protected><OnboardingGate><NotScheduler><BoardBPage /></NotScheduler></OnboardingGate></Protected>} />
          <Route path="/schedule" element={<Protected><OnboardingGate><NotBoardManager><SchedulePage /></NotBoardManager></OnboardingGate></Protected>} />
          <Route path="/setup" element={<Protected><OnboardingGate><NotBoardManager><SetupPage /></NotBoardManager></OnboardingGate></Protected>} />
          {/* /profile stays ungated so a user with no facility can still reach
              their own account — sign out, change password — instead of being
              trapped on the no-access notice. */}
          <Route path="/profile" element={<Protected><NotBoardManager><ProfilePage /></NotBoardManager></Protected>} />
          <Route path="/" element={<Navigate to="/home" replace />} />
          <Route path="*" element={<NotFoundPage />} />
          </Routes>
          </MobileShellGate>
          {/* App-wide 💬 launcher (D-01): one mount, present on every page (except
              /login and /room which has its own inline panel), sharing the single
              MessagingProvider subscription. Inside BrowserRouter for useLocation. */}
          <MessagingLauncher />
        </BrowserRouter>
        </GeofenceGate>
        </MessagingProvider>
        </AlertProvider>
        </ErrorBoundary>
      </AuthProvider>
      </BiometricGate>
    </ToastProvider>
  );
}
